import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Venue } from "../types";
import { apiRequest } from "../config/api";

interface VenueContextType {
  venues: Venue[];
  isLoading: boolean;
  refreshVenues: () => Promise<void>;
}

const VenueContext = createContext<VenueContextType | undefined>(undefined);

interface VenueProviderProps {
  children: ReactNode;
}

export function VenueProvider({ children }: VenueProviderProps) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchVenues = async () => {
    try {
      setIsLoading(true);
      const response = await apiRequest("/api/venues");
      if (response.ok) {
        const data: Venue[] = await response.json();
        setVenues(data);
      }
    } catch (error) {
      console.error("Error fetching venues:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVenues();
  }, []);

  const value: VenueContextType = {
    venues,
    isLoading,
    refreshVenues: fetchVenues,
  };

  return (
    <VenueContext.Provider value={value}>{children}</VenueContext.Provider>
  );
}

export function useVenues() {
  const context = useContext(VenueContext);
  if (context === undefined) {
    throw new Error("useVenues must be used within a VenueProvider");
  }
  return context;
}
